import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ReactLenis } from 'lenis/react';
import 'lenis/dist/lenis.css';

import AuthModal from '../components/AuthModal';
import IntroSequence from './landing/IntroSequence';
import LandingNavigation from './landing/LandingNavigation';
import LandingHero from './landing/LandingHero';
import FeatureShowcase from './landing/FeatureShowcase';
import CinemaRanking from './landing/CinemaRanking';
import CommunityGrid from './landing/CommunityGrid';
import ShopTeaser from './landing/ShopTeaser';
import LandingConversion from './landing/LandingConversion';
import LandingFooter from './landing/LandingFooter';
import { FALLBACK_FEATURES, loadLandingData } from './landing/landingData';
import './landing/LandingPage.css';

gsap.registerPlugin(ScrollTrigger, useGSAP);

function LandingSmoothScroll({ paused }) {
  const lenisRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(pointer: fine)');
    const update = () => setEnabled(!reduced.matches && finePointer.matches);
    update();
    reduced.addEventListener?.('change', update);
    finePointer.addEventListener?.('change', update);
    return () => {
      reduced.removeEventListener?.('change', update);
      finePointer.removeEventListener?.('change', update);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const lenisInstance = lenisRef.current?.lenis;
    const update = (time) => lenisRef.current?.lenis?.raf(time * 1000);
    const sync = () => ScrollTrigger.update();
    gsap.ticker.add(update);
    lenisInstance?.on('scroll', sync);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(update);
      lenisInstance?.off('scroll', sync);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !lenisRef.current?.lenis) return;
    if (paused) lenisRef.current.lenis.stop();
    else lenisRef.current.lenis.start();
  }, [enabled, paused]);

  if (!enabled) return null;
  return <ReactLenis ref={lenisRef} root options={{ autoRaf: false, smoothWheel: true, syncTouch: false, anchors: true }} />;
}

export default function LandingPage({ onLogin, onShowTerms, onShowPrivacy }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scope = useRef(null);
  const [features, setFeatures] = useState(FALLBACK_FEATURES);
  const [ranking, setRanking] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [rankingError, setRankingError] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [registerForm, setRegisterForm] = useState({ username: '', email: '', password: '', accepted: false });
  const [registerStatus, setRegisterStatus] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);

  const refCode = searchParams.get('ref') || '';
  const openAuth = searchParams.get('openAuth') === '1';
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    document.body.classList.add('digital-landing-active');
    return () => document.body.classList.remove('digital-landing-active');
  }, []);

  useEffect(() => {
    if (refCode || openAuth) setShowAuth(true);
  }, [refCode, openAuth]);

  useEffect(() => {
    const controller = new AbortController();
    loadLandingData(controller.signal)
      .then((data) => {
        setFeatures(data.features);
        setRanking(data.ranking);
        setRankingError(data.rankingError);
        setRankingLoading(false);
        window.setTimeout(() => ScrollTrigger.refresh(), 50);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setRankingError(true);
          setRankingLoading(false);
        }
      });
    return () => controller.abort();
  }, []);

  useGSAP(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const progress = document.querySelector('.dl-scroll-progress span');
    const progressTrigger = ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => gsap.set(progress, { scaleX: self.progress }),
    });
    const revealTweens = gsap.utils.toArray('.dl-reveal').map((element) => gsap.fromTo(element,
      { y: 48, autoAlpha: 0, clipPath: 'inset(0 0 18% 0)' },
      { y: 0, autoAlpha: 1, clipPath: 'inset(0 0 0% 0)', duration: 1, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 88%', once: true } }));
    const heroTimeline = gsap.timeline({ delay: 0.25 });
    heroTimeline
      .from('.dl-title-mask b', { yPercent: 115, rotate: 2, duration: 1.1, stagger: 0.1, ease: 'power4.out' })
      .from('.dl-hero-stage', { autoAlpha: 0, y: 70, rotationZ: 2, duration: 1.25, ease: 'power4.out' }, 0.12)
      .from('.dl-route-line path', { strokeDashoffset: 1800, duration: 2, ease: 'power2.out' }, 0.1);
    gsap.to('.dl-hero-stage', { yPercent: 16, ease: 'none', scrollTrigger: { trigger: '.dl-hero', start: 'top top', end: 'bottom top', scrub: 1.1 } });
    return () => {
      progressTrigger.kill();
      revealTweens.forEach((tween) => tween.kill());
    };
  }, { scope });

  const handlePointerMove = (event) => {
    if (!scope.current || !window.matchMedia('(pointer: fine)').matches) return;
    scope.current.style.setProperty('--pointer-x', `${event.clientX}px`);
    scope.current.style.setProperty('--pointer-y', `${event.clientY}px`);
  };

  const handleLoginSuccess = (userData) => {
    onLogin(userData);
    navigate(returnTo?.startsWith('/') ? returnTo : '/social');
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (!registerForm.accepted) {
      setRegisterStatus({ type: 'error', message: 'Zaakceptuj Regulamin i Politykę prywatności.' });
      return;
    }
    if (registerForm.password.length < 6) {
      setRegisterStatus({ type: 'error', message: 'Hasło musi mieć minimum 6 znaków.' });
      return;
    }
    setRegisterLoading(true);
    setRegisterStatus(null);
    try {
      const body = { email: registerForm.email.trim(), username: registerForm.username.trim(), password: registerForm.password, acceptUgcTerms: true };
      if (refCode.trim()) body.referralCode = refCode.trim();
      const response = await fetch('https://v-room.app/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Nie udało się utworzyć konta.');
      setRegisterForm({ username: '', email: '', password: '', accepted: false });
      setRegisterStatus({ type: 'success', message: 'Konto gotowe. Możesz już zalogować się do VROOM!' });
    } catch (error) {
      setRegisterStatus({ type: 'error', message: error.message || 'Błąd połączenia z serwerem.' });
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="digital-landing" ref={scope} onPointerMove={handlePointerMove}>
      <LandingSmoothScroll paused={showAuth} />
      <IntroSequence />
      <div className="dl-pointer-glow" aria-hidden="true" />
      <LandingNavigation onLogin={() => setShowAuth(true)} />
      <main>
        <LandingHero feature={features[0]} onLogin={() => setShowAuth(true)} />
        <FeatureShowcase features={features} />
        <CinemaRanking ranking={ranking} rankingLoading={rankingLoading} rankingError={rankingError} />
        <CommunityGrid />
        <ShopTeaser />
        <LandingConversion form={registerForm} setForm={setRegisterForm} status={registerStatus} loading={registerLoading} onSubmit={handleRegister} onShowTerms={onShowTerms} onShowPrivacy={onShowPrivacy} />
      </main>
      <LandingFooter onShowTerms={onShowTerms} onShowPrivacy={onShowPrivacy} />
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onLogin={handleLoginSuccess} defaultReferralCode={refCode} onShowTerms={onShowTerms} onShowPrivacy={onShowPrivacy} />}
    </div>
  );
}
